import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import {Paper, Typography} from "@mui/material";
import React from "react";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getJSON, postJSON } from "./api";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar, Legend
} from "recharts";
import HourlyVolume from "./components/HourlyVolume";
import WaitHistogram from "./components/WaitHistogram";
import SlaBuckets from "./components/SlaBuckets";
import "./style.css";
import {useMaterialUIController} from "../../context";
const pad = (d) => d.toISOString().slice(0,10);

function Tabs({ tabs, active, onChange }) {
    return (
        <div className="tabs">
            {tabs.map((t) => (
                <button
                    key={t}
                    className={`tab ${active === t ? "active" : ""}`}
                    onClick={() => onChange(t)}
                >
                    {t}
                </button>
            ))}
        </div>
    );
}

/* ---------- Charts / Tables ---------- */
function ChartLine({ data, xKey, lines }) {
    return (
        <div style={{height: 320}}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={xKey} />
                    <YAxis />
                    <Tooltip />
                    {lines.map(k => <Line key={k} type="monotone" dataKey={k} dot={false} />)}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function Table({ columns, rows, keyField }) {
    return (
        <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse'}}>
                <thead>
                <tr>{columns.map(c => (
                    <th key={c.key} style={{textAlign:'left', borderBottom:'1px solid #eee', padding:'8px'}}>{c.title}</th>
                ))}</tr>
                </thead>
                <tbody>
                {rows.map(r => (
                    <tr key={String(r[keyField] ?? Math.random())}>
                        {columns.map(c => (
                            <td key={c.key} style={{borderBottom:'1px solid #f3f3f3', padding:'8px'}}>
                                {c.render ? c.render(r[c.key], r) : String(r[c.key] ?? '')}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------- Heatmap 7×24 ---------- */
function Heatmap({ data }) {
    const max = Math.max(1, ...data.map(d => d.total_incoming||0));
    const cell = (v) => {
        const x = (v||0)/max; // 0..1
        const bg = `rgba(33,150,243,${0.1 + 0.9*x})`;
        return { background:bg, color:'#000' };
    };
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    return (
        <div style={{border:'1px solid #eee', borderRadius:12, overflow:'hidden'}}>
            <div style={{display:'grid', gridTemplateColumns:'80px repeat(24, 1fr)'}}>
                <div style={{background:'#fafafa', padding:6, borderBottom:'1px solid #eee'}}></div>
                {Array.from({length:24},(_,h)=>
                    <div key={h} style={{background:'#fafafa', padding:6, borderBottom:'1px solid #eee', textAlign:'center'}}>{h}</div>
                )}
                {Array.from({length:7},(_,d)=>(
                    <FragmentRow key={d} d={d} days={days} data={data} cell={cell} />
                ))}
            </div>
        </div>
    );
}

function FragmentRow({ d, days, data, cell }) {
    return (
        <>
            <div style={{padding:6, borderRight:'1px solid #eee'}}>{days[d]}</div>
            {Array.from({length:24},(_,h)=>{
                const val = data.find(x => Number(x.dow)===d && Number(x.hour)===h)?.total_incoming || 0;
                return <div key={`c-${d}-${h}`} style={{height:28, ...cell(val), borderRight:'1px solid #fff', borderBottom:'1px solid #fff'}}/>;
            })}
        </>
    );
}


const IntercomReports = () =>
{
    const qc = useQueryClient();

    // טווח ברירת־מחדל: 14 ימים אחרונים
    const [to, setTo] = useState(new Date());
    const [from, setFrom] = useState(() => { const d=new Date(); d.setDate(d.getDate()-14); return d; });
    const range = useMemo(() => ({ from: pad(from), to: pad(to) }), [from,to]);

    const [tab, setTab] = useState('Overview');

    // --- GETs (base) ---
    const kpis = useQuery({ queryKey: ["kpis", range], queryFn: () => getJSON("/api/reports/overview", range) });
    const abd  = useQuery({ queryKey: ["abd",  range], queryFn: () => getJSON("/api/reports/abandoned-daily", range) });
    const sla  = useQuery({ queryKey: ["sla",  range], queryFn: () => getJSON("/api/reports/sla-wait-daily", range) });

    const agents = useQuery({
        queryKey: ["agents", range], queryFn: () => getJSON("/api/reports/agents", range), enabled: tab==="Agents"
    });
    const term = useQuery({
        queryKey: ["term"], queryFn: () => getJSON("/api/reports/termination"), enabled: tab==="Termination"
    });
    const load = useQuery({
        queryKey: ["load"], queryFn: () => getJSON("/api/reports/load-heatmap"), enabled: tab==="Load Heatmap"
    });
    const journeys = useQuery({
        queryKey: ["journeys", range], queryFn: () => getJSON("/api/reports/journeys", range), enabled: tab==="Journeys"
    });
    const callbacks = useQuery({
        queryKey: ["callbacks", range], queryFn: () => getJSON("/api/reports/callbacks", range), enabled: tab==="Callbacks"
    });

    // --- GETs (new tabs) ---
    const hourly = useQuery({
        queryKey: ["hourly", range],
        queryFn: () => getJSON("/api/reports/hourly", range),
        enabled: tab === "Hourly"
    });
    const hist = useQuery({
        queryKey: ["wait-hist", range],
        queryFn: () => getJSON("/api/reports/wait-histogram", { ...range, bin: 5, max: 120 }),
        enabled: tab === "Wait Histogram"
    });
    const slaBuckets = useQuery({
        queryKey: ["sla-buckets", range],
        queryFn: () => getJSON("/api/reports/sla-buckets", { ...range, t1: 10, t2: 20, t3: 30 }),
        enabled: tab === "SLA Buckets"
    });

    // --- ETL עכשיו ---
    const runEtl = useMutation({
        mutationFn: () => postJSON("/api/admin/run-etl", {}),
        onSuccess: () => {
            ["kpis","abd","sla","agents","journeys","callbacks","hourly","wait-hist","sla-buckets"]
                .forEach(k => qc.invalidateQueries({ queryKey: [k] }));
        }
    });

    const last = (kpis.data || []).slice(-1)[0];

    const [controller] = useMaterialUIController();

    const { darkMode } = controller;


    return (<DashboardLayout>
        <DashboardNavbar />
        <div className="container" style={{color: darkMode ? "white" : "black"}}>
            <h1>Intercom Call Reports</h1>

            {/* סינון + ETL */}
            <div className="row">
                <label>From <input type="date" value={range.from} onChange={e=>setFrom(new Date(e.target.value))}/></label>
                <label>To <input type="date" value={range.to} onChange={e=>setTo(new Date(e.target.value))}/></label>
                <button onClick={() => runEtl.mutate()} disabled={runEtl.isPending}>
                    {runEtl.isPending ? "Running ETL..." : "Run ETL now"}
                </button>
            </div>

            <Tabs
                tabs={["Overview","Agents","Termination","Load Heatmap","Journeys","Callbacks","Hourly","Wait Histogram","SLA Buckets"]}
                active={tab}
                onChange={setTab}
            />

            {tab === "Overview" && (
                <>
                    <div className="row section">
                        <div className="card">
                            <div className="kpi-title">Abandoned (last day)</div>
                            <div className="kpi-value">{last?.abandoned_calls ?? "-"}</div>
                        </div>
                        <div className="card">
                            <div className="kpi-title">Incoming (last day)</div>
                            <div className="kpi-value">{last?.total_incoming ?? "-"}</div>
                        </div>
                        <div className="card">
                            <div className="kpi-title">Abandon Rate (last day)</div>
                            <div className="kpi-value">{last ? `${last.abandon_rate}%` : "-"}</div>
                        </div>
                        <div className="card">
                            <div className="kpi-title">Days in range</div>
                            <div className="kpi-value">{kpis.data?.length ?? "-"}</div>
                        </div>
                    </div>

                    <div className="section">
                        <h3>Abandoned by Day</h3>
                        <ChartLine data={abd.data ?? []} xKey="day" lines={["abandoned"]} />
                    </div>

                    <div className="section">
                        <h3>SLA (p50/avg/p90) by Day</h3>
                        <ChartLine
                            data={sla.data ?? []}
                            xKey="day"
                            lines={["p50_answer_s", "avg_answer_s", "p90_answer_s"]}
                        />
                    </div>
                </>
            )}

            {tab==="Agents" && (
                <div className="section">
                    <h3>Agents (daily aggregated)</h3>
                    <Table
                        keyField="agent"
                        columns={[
                            {key:"day", title:"Day"},
                            {key:"agent", title:"Agent"},
                            {key:"total_calls", title:"Total"},
                            {key:"answered_calls", title:"Answered"},
                            {key:"answer_rate_pct", title:"Answer %", render:(v)=> v!=null ? `${v}%` : "-"},
                            {key:"avg_answer_wait_s", title:"Avg Wait (s)"},
                            {key:"aht_seconds", title:"AHT (s)"}
                        ]}
                        rows={agents.data ?? []}
                    />
                </div>
            )}

            {tab==="Termination" && (
                <div className="section">
                    <h3>Termination Reasons</h3>
                    <Table
                        keyField="termination_reason"
                        columns={[
                            {key:"termination_reason", title:"Reason"},
                            {key:"total", title:"Total"},
                            {key:"abandoned", title:"Abandoned"}
                        ]}
                        rows={term.data ?? []}
                    />
                    <div className="section" style={{height:320}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={term.data ?? []}>
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis dataKey="termination_reason" interval={0} angle={-20} textAnchor="end" height={70}/>
                                <YAxis/>
                                <Tooltip/>
                                <Legend/>
                                <Bar dataKey="total" />
                                <Bar dataKey="abandoned" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {tab==="Load Heatmap" && (
                <div className="section">
                    <h3>Load Heatmap (total incoming)</h3>
                    <Heatmap data={load.data ?? []}/>
                </div>
            )}

            {tab==="Journeys" && (
                <div className="section">
                    <h3>Journeys (daily)</h3>
                    <Table
                        keyField="day"
                        columns={[
                            {key:"day", title:"Day"},
                            {key:"abandoned_journeys", title:"Abandoned Journeys"},
                            {key:"total_journeys", title:"Total Journeys"},
                            {key:"abandon_rate_pct", title:"Abandon %", render:(v)=> v!=null ? `${v}%` : "-"},
                            {key:"top_depth", title:"Max Depth"}
                        ]}
                        rows={journeys.data ?? []}
                    />
                </div>
            )}

            {tab==="Callbacks" && (
                <div className="section">
                    <h3>Callbacks (daily)</h3>
                    <Table
                        keyField="day"
                        columns={[
                            {key:"day", title:"Day"},
                            {key:"abandoned_total", title:"Abandoned Total"},
                            {key:"abandoned_with_callback", title:"With Callback"},
                            {key:"callback_rate_pct", title:"Callback %", render:(v)=> v!=null ? `${v}%` : "-"}
                        ]}
                        rows={callbacks.data ?? []}
                    />
                </div>
            )}

            {tab==="Hourly" && (
                <div className="section">
                    <h3>Hourly Volume (Answered vs Abandoned)</h3>
                    <HourlyVolume data={hourly.data ?? []}/>
                </div>
            )}

            {tab==="Wait Histogram" && (
                <div className="section">
                    <h3>Wait Time Histogram (answered)</h3>
                    <WaitHistogram data={hist.data ?? []} bin={5}/>
                </div>
            )}

            {tab==="SLA Buckets" && (
                <div className="section">
                    <h3>SLA Buckets (range totals)</h3>
                    <SlaBuckets stats={slaBuckets.data ?? {}} t1={10} t2={20} t3={30}/>
                </div>
            )}
        </div>
        );
    </DashboardLayout>);
}

export default IntercomReports;